import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';

// `results` is an array of { name, status, message?, products?, errors? }
//   - status: 'ok' | 'error' | 'partial'
//   - products: optional array of child items that succeeded (e.g. per-color products)
//   - errors:  optional array of { color/field, message } for items that failed
//
// Use this after a bulk push or sync to give the user a per-item breakdown.
const PushResultsDialog = ({ open, onClose, title = 'Push results', results = [] }) => {
  const [expanded, setExpanded] = useState({});
  const total = results.length;
  const succeeded = results.filter((r) => r.status === 'ok').length;
  const partial = results.filter((r) => r.status === 'partial').length;
  const failed = results.filter((r) => r.status === 'error').length;

  const totalChildren = results.reduce((s, r) => s + (r.products?.length || 0), 0);
  const totalErrors = results.reduce((s, r) => s + (r.errors?.length || 0), 0);

  const statusBg = { ok: 'rgba(0,217,166,0.10)', partial: 'rgba(255,152,0,0.10)', error: 'rgba(239,68,68,0.10)' };
  const statusColor = { ok: '#00A878', partial: '#FF9800', error: '#EF4444' };
  const statusIcon = {
    ok: <CheckCircleRounded sx={{ fontSize: 18, color: statusColor.ok }} />,
    partial: <WarningAmberRounded sx={{ fontSize: 18, color: statusColor.partial }} />,
    error: <ErrorRounded sx={{ fontSize: 18, color: statusColor.error }} />,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`Total ${total}`} />
          {succeeded > 0 && <Chip label={`${succeeded} succeeded`} sx={{ bgcolor: statusBg.ok, color: statusColor.ok }} />}
          {partial > 0 && <Chip label={`${partial} partial`} sx={{ bgcolor: statusBg.partial, color: statusColor.partial }} />}
          {failed > 0 && <Chip label={`${failed} failed`} sx={{ bgcolor: statusBg.error, color: statusColor.error }} />}
          {totalChildren > 0 && <Chip variant="outlined" label={`${totalChildren} child product(s)`} />}
          {totalErrors > 0 && <Chip variant="outlined" label={`${totalErrors} field error(s)`} />}
        </Box>
        {results.length === 0 ? (
          <Typography color="text.secondary">No results to display.</Typography>
        ) : (
          <Box>
            {results.map((r, i) => {
              const isExpanded = !!expanded[i];
              const hasDetails = (r.products && r.products.length > 0) || (r.errors && r.errors.length > 0) || r.message;
              return (
                <Box
                  key={i}
                  sx={{
                    border: '1px solid', borderColor: 'divider',
                    borderRadius: 1, mb: 1, bgcolor: statusBg[r.status] || 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                    {statusIcon[r.status]}
                    <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{r.name}</Typography>
                    {r.products && r.products.length > 0 && (
                      <Typography variant="caption" color="text.secondary">{r.products.length} product(s)</Typography>
                    )}
                    {r.errors && r.errors.length > 0 && (
                      <Typography variant="caption" sx={{ color: statusColor.error }}>{r.errors.length} error(s)</Typography>
                    )}
                    {hasDetails && (
                      <IconButton size="small" onClick={() => setExpanded((s) => ({ ...s, [i]: !s[i] }))}>
                        {isExpanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
                      </IconButton>
                    )}
                  </Box>
                  <Collapse in={isExpanded}>
                    <Box sx={{ px: 2, pb: 1.5, fontSize: '0.85rem' }}>
                      {r.message && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{r.message}</Typography>
                      )}
                      {r.products && r.products.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>Created / updated</Typography>
                          {r.products.map((p, j) => (
                            <Typography key={j} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              • {p.color || p.title || p.name || JSON.stringify(p)}
                              {p.shopify_product_id ? ` (Shopify ${p.shopify_product_id})` : ''}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {r.errors && r.errors.length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: statusColor.error }}>Errors</Typography>
                          {r.errors.map((e, j) => (
                            <Typography key={j} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              • <strong>{e.color || e.key || e.field || 'item'}:</strong> {e.message}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PushResultsDialog;
